// authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Storage } from '@capacitor/storage';
import { Capacitor } from '@capacitor/core';

// Helper function to store auth data based on platform
const storeAuthData = async (token, user, refreshToken = null) => {
  const isNative = Capacitor.isNativePlatform();
  
  if (isNative) {
    await Storage.set({ key: 'token', value: token });
    await Storage.set({ key: 'user', value: JSON.stringify(user) });
    if (refreshToken) {
      await Storage.set({ key: 'refreshToken', value: refreshToken });
    }
  } else {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
  }
};

// Helper function to clear auth data based on platform
const clearAuthData = async () => {
  const isNative = Capacitor.isNativePlatform();
  
  if (isNative) {
    await Storage.remove({ key: 'token' });
    await Storage.remove({ key: 'user' });
    await Storage.remove({ key: 'refreshToken' });
  } else {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
};

// Helper function to get auth data based on platform
const getAuthData = async () => {
  const isNative = Capacitor.isNativePlatform();
  
  if (isNative) {
    const { value: token } = await Storage.get({ key: 'token' });
    const { value: user } = await Storage.get({ key: 'user' });
    const { value: refreshToken } = await Storage.get({ key: 'refreshToken' });
    return { token, user: user ? JSON.parse(user) : null, refreshToken };
  } else {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    return { token, user: user ? JSON.parse(user) : null, refreshToken: null };
  }
};

export const loginWithToken = createAsyncThunk(
  'auth/loginWithToken',
  async ({ token, userFromCallback, refreshTokenForNative = null }, { rejectWithValue }) => {
    try {
      let userToStore = userFromCallback;
      if (!userToStore) {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/user`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        userToStore = response.data;
      }
      
      await storeAuthData(token, userToStore, refreshTokenForNative);
      return { user: userToStore, token, refreshToken: refreshTokenForNative };
    } catch (error) {
      console.error('❌ Login with token failed:', error.response?.data || error.message);
      await clearAuthData();
      return rejectWithValue(error.response?.data || 'Failed to login with token');
    }
  }
);

export const restoreAuthState = createAsyncThunk(
  'auth/restoreAuthState',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { token, user, refreshToken } = await getAuthData();
      
      if (token && user) {
        return await dispatch(loginWithToken({ token, userFromCallback: user, refreshTokenForNative: refreshToken })).unwrap();
      }
      
      return rejectWithValue('No auth data found');
    } catch (error) {
      console.error('❌ Restore auth state failed:', error);
      await clearAuthData();
      return rejectWithValue(error.message || 'Failed to restore auth state');
    }
  }
);

export const logoutUser = createAsyncThunk('auth/logoutUser', async (_, { dispatch }) => {
  try {
    await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/logout`, {}, { withCredentials: true });
  } catch (error) {
    console.error("Logout API call failed, proceeding with client-side cleanup:", error);
  }
  await clearAuthData();
  dispatch(clearUserAndToken());
  return null;
});

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post('https://fastapi-backend-657266059621.asia-northeast1.run.app/api/auth/login', credentials);
      const { access_token, user, refresh_token } = response.data;
      await storeAuthData(access_token, user, refresh_token);
      return { user, token: access_token, refreshToken: refresh_token };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProfilePicture = createAsyncThunk(
  'auth/updateProfilePicture',
  async (profilePicUrl, { getState }) => {
    const { auth } = getState();
    const updatedUser = {
      ...auth.user,
      profile_pic: profilePicUrl
    };
    
    await storeAuthData(auth.token, updatedUser);
    return profilePicUrl;
  }
);

// New async thunk for updating user and session after phone verification or similar updates
export const updateUserAndSessionAfterVerification = createAsyncThunk(
  'auth/updateUserAndSessionAfterVerification',
  async ({ user, token, refreshToken: newRefreshToken = null }, { getState, rejectWithValue }) => {
    try {
      const { refreshToken: currentRefreshToken } = getState().auth; // Get existing refresh token
      const refreshTokenToStore = newRefreshToken || currentRefreshToken;
      await storeAuthData(token, user, refreshTokenToStore);
      return { user, token, refreshToken: refreshTokenToStore };
    } catch (error) {
      console.error('❌ Failed to store user and session data after verification:', error);
      return rejectWithValue(error.message || 'Failed to update user and session');
    }
  }
);

export const refreshAccessToken = createAsyncThunk(
  'auth/refreshAccessToken',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const { refreshToken: currentRefreshToken, user: currentUser } = getState().auth; // Get current user as well

    if (!currentRefreshToken) {
      console.warn('⚠️ No refresh token available for refreshing access token.');
      return rejectWithValue('No refresh token');
    }

    try {
      // Direct axios call to avoid circular dependency with services/api.js
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/refresh-token`,
        { refresh_token: currentRefreshToken }, // Backend expects { "refresh_token": "value" }
        { withCredentials: true } // To send HttpOnly cookie if backend uses it for refresh
      );

      const { access_token, user: updatedUser, refresh_token: newRefreshTokenFromResponse } = response.data;
      
      const userToStore = updatedUser || currentUser; // Prefer updated user, fallback to current
      const finalNewRefreshToken = newRefreshTokenFromResponse || currentRefreshToken; // Prefer new rotated token

      // Store all tokens and user info
      await storeAuthData(access_token, userToStore, finalNewRefreshToken);

      // Dispatch action to update token and user in Redux state
      dispatch(setUserAndToken({ 
        user: userToStore, 
        token: access_token, 
        refreshToken: finalNewRefreshToken 
      }));
      
      // Return all relevant data for the interceptor in api.js
      return { access_token, user: userToStore, refresh_token: finalNewRefreshToken }; 

    } catch (error) {
      console.error('❌ Failed to refresh access token from thunk:', error.response?.data || error.message);
      // If refresh fails, logout the user. logoutUser thunk handles clearing data.
      // Adding a check to prevent infinite loops if logoutUser itself fails or causes another refresh attempt.
      if (getState().auth.isAuthenticated) { // Only dispatch logout if still authenticated
        dispatch(logoutUser());
      }
      return rejectWithValue(error.response?.data || 'Failed to refresh token');
    }
  }
);

// Initialize state based on platform
const getInitialState = () => {
  const isNative = Capacitor.isNativePlatform();
  let token = null;
  let user = null;
  
  if (isNative) {
    // For native platforms, we'll load the state asynchronously
    return {
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isGuest: false,
      isLoading: true,
      error: null
    };
  } else {
    // For web, we can load from localStorage immediately
    token = localStorage.getItem('token');
    user = localStorage.getItem('user');
    return {
      user: user ? JSON.parse(user) : null,
      token,
      refreshToken: null,
      isAuthenticated: !!token,
      isGuest: false,
      isLoading: false,
      error: null
    };
  }
};

const initialState = getInitialState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUserAndToken: (state, action) => {
      const { user, token, refreshToken } = action.payload;
      state.user = user;
      state.token = token;
      state.isAuthenticated = true;
      state.isGuest = false;
      state.error = null;
      state.isLoading = false;
      if (Capacitor.isNativePlatform()) {
        state.refreshToken = refreshToken;
      }
    },
    clearUserAndToken: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isGuest = false;
      state.error = null;
      state.isLoading = false;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    setGuestLogin: (state, action) => {
      state.user = action.payload;
      state.isGuest = true;
      state.isAuthenticated = false;
      state.token = null;
      state.refreshToken = null;
      state.isLoading = false;
    },
    updateUserProfile: (state, action) => {
      if (state.user && !state.isGuest) {
        state.user = {
          ...state.user,
          ...action.payload
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginWithToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithToken.fulfilled, (state, action) => {
        const { user, token, refreshToken } = action.payload;
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = user;
        state.token = token;
        state.isGuest = false;
        state.refreshToken = Capacitor.isNativePlatform() ? refreshToken : null;
      })
      .addCase(loginWithToken.rejected, (state, action) => {
        console.error('❌ Redux: Login with token rejected:', action.payload);
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.isGuest = false;
        state.refreshToken = null;
      })
      .addCase(restoreAuthState.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(restoreAuthState.fulfilled, (state, action) => {
        const { user, token, refreshToken } = action.payload;
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = user;
        state.token = token;
        state.refreshToken = Capacitor.isNativePlatform() ? refreshToken : null;
      })
      .addCase(restoreAuthState.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.error = action.payload;
      })
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        // State is cleared by clearUserAndToken reducer, called in the thunk
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
      })
      .addCase(updateProfilePicture.fulfilled, (state, action) => {
        state.user.profile_pic = action.payload;
      })
      // Add case for the new thunk
      .addCase(updateUserAndSessionAfterVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserAndSessionAfterVerification.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken || state.refreshToken;
        state.isAuthenticated = true;
        state.isLoading = false;
      })
      .addCase(updateUserAndSessionAfterVerification.rejected, (state, action) => {
        console.log('❌ Redux: Update user and session after verification rejected:', action.payload);
        state.isLoading = false;
        state.error = action.payload;
      })
      // Cases for refreshAccessToken
      .addCase(refreshAccessToken.pending, (state) => {
        // Optionally set a specific loading flag for token refresh
        // state.isRefreshingToken = true; 
      })
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        // State is updated by the setUserAndToken dispatch within the thunk itself.
        // If not, update here:
        // state.token = action.payload.access_token;
        // state.user = action.payload.user;
        // state.refreshToken = action.payload.refresh_token;
        // state.isAuthenticated = true;
        // state.isRefreshingToken = false;
      })
      .addCase(refreshAccessToken.rejected, (state, action) => {
        console.log('❌ Redux: Refresh access token rejected:', action.payload);
        // State is updated by the logoutUser dispatch within the thunk's catch block.
        // If not, ensure user is logged out:
        // state.isAuthenticated = false;
        // state.token = null;
        // state.user = null;
        // state.refreshToken = null;
        // state.isRefreshingToken = false;
        // state.error = action.payload || 'Token refresh failed';
      });
  },
});

export const {
  setUserAndToken,
  clearUserAndToken,
  setLoading,
  setError,
  setGuestLogin,
  updateUserProfile
} = authSlice.actions;

export default authSlice.reducer;
